import React, { ForwardRefRenderFunction, Suspense, useEffect } from 'react'

import { getLazyComponent } from './get-lazy-component'
import {
  PageComponentProps,
  RegisterPageOptions,
  ShouldPageRenderHook,
  ShouldRenderRef,
} from './types'

import { FullscreenSpinnerPage } from '@/components/shared/FullscreenSpinner'

export function createPageComponent<T>(
  useShouldRender: ShouldPageRenderHook<T>,
  options: RegisterPageOptions<T>,
): ForwardRefRenderFunction<ShouldRenderRef, PageComponentProps & T> {
  const Component = getLazyComponent(options.pageFactory)

  return function PageComponent({ isOptional = false, ...pageProps }, ref) {
    const shouldRender = useShouldRender(
      isOptional,
      pageProps as Extract<typeof pageProps, T>,
    )

    useEffect(() => {
      if (ref && 'current' in ref)
        ref.current?.onShouldRenderChange?.(shouldRender)
    }, [ref, shouldRender])

    return (
      <>
        {shouldRender ? (
          <Suspense fallback={<FullscreenSpinnerPage />}>
            <Component {...pageProps} />
          </Suspense>
        ) : (
          <FullscreenSpinnerPage />
        )}
      </>
    )
  }
}
